import { z } from 'zod';
import { registry } from '../registry';
import { baseLastBowelMovement, baseLastBowelMovementRefined } from './last-bowel-movement';
/** Card titles used by the Bladder/Bowel (GI/GU) UI — single source for edit + read-only. */
export const bladderBowelCardTitles = {
    giSymptoms: 'GI Symptoms',
    abdomen: 'Abdomen',
    bowelSounds: 'Bowel Sounds',
    pain: 'Pain',
    lastBowelMovement: 'Last Bowel Movement',
    weightChange: 'Recent Weight Change',
    giClinicalNarrative: 'GI Clinical Narrative',
    genitourinary: 'Genitourinary',
    guClinicalNarrative: 'GU Clinical Narrative',
};
/** @deprecated Prefer `bladderBowelCardTitles` */
export const giGuCardTitles = bladderBowelCardTitles;
export const baseGISymptoms = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    nauseaVomiting: z
        .string()
        .optional()
        .register(registry, {
        label: 'Nausea / Vomiting',
        options: [
            { value: 'none', label: 'None' },
            { value: 'nauseaOnly', label: 'Nausea Only' },
            { value: 'persistent', label: 'Persistent' },
            { value: 'vomiting', label: 'Vomiting' },
        ],
    }),
    vomitingTimesPerDay: z.string().optional().register(registry, { label: 'Times / Day' }),
    vomitingDaysDuration: z.string().optional().register(registry, { label: 'Days Duration' }),
    vomitingCharacteristics: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Vomiting Characteristics',
        options: [
            { value: 'bilious', label: 'Bilious' },
            { value: 'undigestedFood', label: 'Undigested Food' },
            { value: 'bloodTinged', label: 'Blood-Tinged' },
        ],
    }),
    bowelHabits: z
        .array(z.string())
        .optional()
        .register(registry, {
        label: 'Bowel Habits',
        options: [
            { value: 'none', label: 'None' },
            { value: 'constipation', label: 'Constipation' },
            { value: 'diarrhea', label: 'Diarrhea' },
            { value: 'fecalIncontinence', label: 'Fecal Incontinence' },
            { value: 'bloatingGas', label: 'Bloating / Gas' },
            { value: 'cramping', label: 'Cramping' },
            { value: 'rectalPain', label: 'Rectal Pain' },
            { value: 'hemorrhoids', label: 'Hemorrhoids' },
        ],
    }),
    giBleedingType: z
        .string()
        .optional()
        .register(registry, {
        label: 'GI Bleeding',
        options: [
            { value: 'none', label: 'None' },
            { value: 'giBleedingActive', label: 'GI Bleeding' },
            { value: 'hematemesis', label: 'Hematemesis' },
        ],
    }),
    giBleedingMdNotification: z.string().optional().register(registry, { label: 'MD Notification Required' }),
    otherGiFindings: z
        .string()
        .optional()
        .register(registry, {
        label: 'Other GI Findings',
        options: [
            { value: 'none', label: 'None' },
            { value: 'heartburnGerd', label: 'Heartburn / GERD' },
            { value: 'dyspepsia', label: 'Dyspepsia' },
            { value: 'earlySatiety', label: 'Early Satiety' },
            { value: 'flatulenceExcessive', label: 'Flatulence' },
        ],
    }),
})
    .register(registry, { label: bladderBowelCardTitles.giSymptoms });
export const baseGISymptomsRefined = baseGISymptoms.refine((data) => data.wnl ||
    data.nauseaVomiting?.trim() ||
    data.vomitingTimesPerDay?.trim() ||
    data.vomitingDaysDuration?.trim() ||
    data.vomitingCharacteristics?.length ||
    data.bowelHabits?.length ||
    data.giBleedingType?.trim() ||
    data.giBleedingMdNotification?.trim() ||
    data.otherGiFindings?.trim(), { message: 'At least one answer is required', path: [] });
export const baseAbdomenSchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    contour: z
        .string()
        .optional()
        .register(registry, {
        label: 'Contour',
        options: [
            { value: 'flat', label: 'Flat' },
            { value: 'rounded', label: 'Rounded' },
            { value: 'distended', label: 'Distended' },
            { value: 'scaphoid', label: 'Scaphoid' },
        ],
    }),
    girth: z.string().optional().register(registry, { label: 'Girth' }),
    ascitesPresent: z.boolean().optional().register(registry, { label: 'Ascites Present' }),
    tenderness: z
        .string()
        .optional()
        .register(registry, {
        label: 'Tenderness',
        options: [
            { value: 'none', label: 'None' },
            { value: 'localized', label: 'Localized' },
            { value: 'diffuse', label: 'Diffuse' },
            { value: 'rebound', label: 'Rebound' },
        ],
    }),
    tendernessMdNotification: z.string().optional().register(registry, { label: 'MD Notification Required' }),
    additionalFindings: z
        .string()
        .optional()
        .register(registry, {
        label: 'Additional Findings',
        options: [
            { value: 'none', label: 'None' },
            { value: 'abdominalRigidity', label: 'Abdominal Rigidity' },
            { value: 'guarding', label: 'Guarding' },
            { value: 'palpableMass', label: 'Palpable Mass' },
        ],
    }),
    rigidityMdNotification: z.string().optional().register(registry, { label: 'MD Notification Required' }),
})
    .register(registry, { label: bladderBowelCardTitles.abdomen });
export const baseAbdomenSchemaRefined = baseAbdomenSchema.refine((data) => data.wnl ||
    data.contour?.trim() ||
    data.girth?.trim() ||
    data.ascitesPresent ||
    data.tenderness?.trim() ||
    data.tendernessMdNotification?.trim() ||
    data.additionalFindings?.trim() ||
    data.rigidityMdNotification?.trim(), { message: 'At least one answer is required', path: [] });
const bowelSoundOptions = [
    { value: 'present', label: 'Present' },
    { value: 'hyperactive', label: 'Hyperactive' },
    { value: 'hypoactive', label: 'Hypoactive' },
    { value: 'absent', label: 'Absent' },
];
export const baseBowelSoundsSchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    ruq: z
        .string()
        .optional()
        .register(registry, { label: 'RUQ', options: [...bowelSoundOptions] }),
    luq: z
        .string()
        .optional()
        .register(registry, { label: 'LUQ', options: [...bowelSoundOptions] }),
    rlq: z
        .string()
        .optional()
        .register(registry, { label: 'RLQ', options: [...bowelSoundOptions] }),
    llq: z
        .string()
        .optional()
        .register(registry, { label: 'LLQ', options: [...bowelSoundOptions] }),
})
    .register(registry, { label: bladderBowelCardTitles.bowelSounds });
export const baseBowelSoundsSchemaRefined = baseBowelSoundsSchema.refine((data) => data.wnl || data.ruq?.trim() || data.luq?.trim() || data.rlq?.trim() || data.llq?.trim(), { message: 'At least one answer is required', path: [] });
const giPainOptions = [
    { value: 'absent', label: 'Absent' },
    { value: 'dull', label: 'Dull' },
    { value: 'sharp', label: 'Sharp' },
    { value: 'crampy', label: 'Crampy' },
];
export const baseGiPainSchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    ruq: z
        .string()
        .optional()
        .register(registry, { label: 'RUQ', options: [...giPainOptions] }),
    luq: z
        .string()
        .optional()
        .register(registry, { label: 'LUQ', options: [...giPainOptions] }),
    rlq: z
        .string()
        .optional()
        .register(registry, { label: 'RLQ', options: [...giPainOptions] }),
    llq: z
        .string()
        .optional()
        .register(registry, { label: 'LLQ', options: [...giPainOptions] }),
})
    .register(registry, { label: bladderBowelCardTitles.pain });
export const baseGiPainSchemaRefined = baseGiPainSchema.refine((data) => data.wnl || data.ruq?.trim() || data.luq?.trim() || data.rlq?.trim() || data.llq?.trim(), { message: 'At least one answer is required', path: [] });
export const baseWeightChangeSchema = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    change: z
        .string()
        .optional()
        .register(registry, {
        label: 'Weight Change',
        options: [
            { value: 'stable', label: 'Stable' },
            { value: 'loss5Less', label: 'Loss <5 lbs' },
            { value: 'loss5More', label: 'Loss >5 lbs' },
            { value: 'gain5Less', label: 'Gain <5 lbs' },
            { value: 'gain5More', label: 'Gain >5 lbs' },
        ],
    }),
})
    .register(registry, { label: bladderBowelCardTitles.weightChange });
export const baseWeightChangeSchemaRefined = baseWeightChangeSchema.refine((data) => data.wnl || data.change?.trim(), {
    message: 'At least one answer is required',
    path: [],
});
export const baseGiClinicalNarrativeSection = z
    .object({
    wnl: z.boolean().optional().register(registry, { label: 'WNL' }),
    narrative: z.string().optional().register(registry, { label: 'Narrative' }),
})
    .register(registry, { label: bladderBowelCardTitles.giClinicalNarrative });
export const baseClinicalNarrativeSchemaRefined = baseGiClinicalNarrativeSection.refine((data) => data.wnl || data.narrative?.trim(), {
    message: 'At least one answer is required',
    path: [],
});
export const baseGastrointestinalSchema = z.object({
    lastBowelMovement: baseLastBowelMovement,
    giSymptoms: baseGISymptoms.optional(),
    abdomen: baseAbdomenSchema.optional(),
    bowelSounds: baseBowelSoundsSchema.optional(),
    pain: baseGiPainSchema.optional(),
    weightChange: baseWeightChangeSchema.optional(),
    clinicalNarrative: baseGiClinicalNarrativeSection.optional(),
});
export const baseGastrointestinalSchemaRefined = z.object({
    lastBowelMovement: baseLastBowelMovementRefined,
    giSymptoms: baseGISymptomsRefined,
    abdomen: baseAbdomenSchemaRefined,
    bowelSounds: baseBowelSoundsSchemaRefined,
    pain: baseGiPainSchemaRefined,
    weightChange: baseWeightChangeSchemaRefined,
    clinicalNarrative: baseClinicalNarrativeSchemaRefined,
});
